import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Input,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";

import {
  downloadProductBatchRecallCsv,
  getProductBatchRecall,
  notifyProductBatchRecall,
  triggerBatchRecallCsvDownload,
} from "../../services/productService";
import { InventoryBatchRecallClient } from "../../types/inventoryBatchRecall";

type ProductBatchRecallOffcanvasProps = {
  isOpen: boolean;
  productId: number;
  batchId: number | null;
  batchLabel?: string;
  onClose: () => void;
};

const formatPhone = (
  countryCode: string | null,
  phoneNumber: string | null
): string => {
  if (!countryCode || !phoneNumber) {
    return "—";
  }

  return `+${countryCode} ${phoneNumber}`;
};

const ProductBatchRecallOffcanvas = ({
  isOpen,
  productId,
  batchId,
  batchLabel,
  onClose,
}: ProductBatchRecallOffcanvasProps) => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<InventoryBatchRecallClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [hasPhoneOnly, setHasPhoneOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [matchedCount, setMatchedCount] = useState(0);
  const [internalWithoutClient, setInternalWithoutClient] = useState(0);
  const [lgpdNotice, setLgpdNotice] = useState("");
  const [loadError, setLoadError] = useState(false);

  const filterParams = useCallback(
    () => ({
      q: appliedSearch || undefined,
      has_phone: hasPhoneOnly ? 1 : undefined,
    }),
    [appliedSearch, hasPhoneOnly]
  );

  const load = useCallback(async () => {
    if (!isOpen || batchId === null) {
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await getProductBatchRecall(productId, batchId, {
        page,
        per_page: 20,
        ...filterParams(),
      });
      setItems(response.data ?? []);
      setLastPage(response.meta?.last_page ?? 1);
      setMatchedCount(response.meta?.matched_count ?? response.meta?.total ?? 0);
      setInternalWithoutClient(
        response.meta?.internal_without_client_count ?? 0
      );
      setLgpdNotice(
        response.meta?.lgpd_notice ||
          t("studio.products.batches.recall.lgpdBanner")
      );
    } catch {
      setItems([]);
      setLoadError(true);
      toast.error(t("studio.products.batches.recall.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [batchId, filterParams, isOpen, page, productId, t]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setAppliedSearch("");
      setHasPhoneOnly(false);
      setPage(1);
      setItems([]);
      setLoadError(false);
      return;
    }

    void load();
  }, [isOpen, load]);

  const formatDateTime = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const handleToggleHasPhone = () => {
    setPage(1);
    setHasPhoneOnly((prev) => !prev);
  };

  const handleExport = async () => {
    if (batchId === null) {
      return;
    }

    setExporting(true);
    try {
      const blob = await downloadProductBatchRecallCsv(productId, batchId, {
        ...filterParams(),
      });
      triggerBatchRecallCsvDownload(blob, `lote-recall-${batchId}.csv`);
      toast.success(t("studio.products.batches.recall.toast.exportSuccess"));
    } catch {
      toast.error(t("studio.products.batches.recall.toast.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const handleCopyNames = async () => {
    if (batchId === null) {
      return;
    }

    setCopying(true);
    try {
      const names: string[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await getProductBatchRecall(productId, batchId, {
          page: currentPage,
          per_page: 100,
          ...filterParams(),
        });
        for (const row of response.data ?? []) {
          if (row.name?.trim()) {
            names.push(row.name.trim());
          }
        }
        totalPages = response.meta?.last_page ?? 1;
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (names.length === 0) {
        toast.info(t("studio.products.batches.recall.toast.copyEmpty"));
        return;
      }

      await navigator.clipboard.writeText(names.join("\n"));
      toast.success(
        t("studio.products.batches.recall.toast.copySuccess", {
          count: names.length,
        })
      );
    } catch {
      toast.error(t("studio.products.batches.recall.toast.copyError"));
    } finally {
      setCopying(false);
    }
  };

  const handleCopyNamesAndPhones = async () => {
    if (batchId === null) {
      return;
    }

    setCopying(true);
    try {
      const contacts: string[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await getProductBatchRecall(productId, batchId, {
          page: currentPage,
          per_page: 100,
          ...filterParams(),
        });
        for (const row of response.data ?? []) {
          if (row.name?.trim()) {
            const phone = formatPhone(row.country_code, row.phone_number);
            if (phone && phone !== "—") {
              contacts.push(`${row.name.trim()} — ${phone}`);
            }
          }
        }
        totalPages = response.meta?.last_page ?? 1;
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (contacts.length === 0) {
        toast.info(t("studio.products.batches.recall.toast.copyEmpty"));
        return;
      }

      await navigator.clipboard.writeText(contacts.join("\n"));
      toast.success(
        t("studio.products.batches.recall.toast.copyNamesPhonesSuccess", {
          count: contacts.length,
        })
      );
    } catch {
      toast.error(t("studio.products.batches.recall.toast.copyError"));
    } finally {
      setCopying(false);
    }
  };

  const handleNotifyTeam = async () => {
    if (batchId === null) {
      return;
    }

    try {
      const response = await notifyProductBatchRecall(productId, batchId);
      toast.success(
        response.message ||
          t("studio.products.batches.recall.toast.notifySuccess")
      );
    } catch {
      toast.error(t("studio.products.batches.recall.toast.notifyError"));
    }
  };

  const emptyMessage = hasPhoneOnly
    ? t("studio.products.batches.recall.emptyWithPhone")
    : t("studio.products.batches.recall.empty");

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" className="border-0">
      <OffcanvasHeader toggle={onClose} className="bg-light">
        {t("studio.products.batches.recall.title")}
        {batchLabel ? (
          <small className="d-block text-muted fw-normal mt-1">{batchLabel}</small>
        ) : null}
      </OffcanvasHeader>
      <OffcanvasBody>
        <Alert color="info" className="mb-3">
          {lgpdNotice || t("studio.products.batches.recall.lgpdBanner")}
        </Alert>

        <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
          <Input
            type="search"
            bsSize="sm"
            style={{ maxWidth: 220 }}
            placeholder={t("studio.products.batches.recall.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSearch();
              }
            }}
          />
          <Button color="secondary" size="sm" outline onClick={handleSearch}>
            {t("studio.products.batches.recall.actions.search")}
          </Button>
          <Button
            color="primary"
            size="sm"
            outline
            disabled={exporting || loading}
            onClick={() => void handleExport()}
          >
            {exporting ? (
              <Spinner size="sm" />
            ) : (
              t("studio.products.batches.recall.actions.exportCsv")
            )}
          </Button>
          <Button
            color="secondary"
            size="sm"
            outline
            disabled={copying || loading || matchedCount === 0}
            onClick={() => void handleCopyNames()}
          >
            {copying ? (
              <Spinner size="sm" />
            ) : (
              t("studio.products.batches.recall.actions.copyNames")
            )}
          </Button>
          <Button
            color="secondary"
            size="sm"
            outline
            disabled={copying || loading || matchedCount === 0}
            onClick={() => void handleCopyNamesAndPhones()}
          >
            {copying ? (
              <Spinner size="sm" />
            ) : (
              t("studio.products.batches.recall.actions.copyNamesPhones")
            )}
          </Button>
          <Button
            color="primary"
            size="sm"
            disabled={loading || matchedCount === 0}
            onClick={() => void handleNotifyTeam()}
          >
            <i className="ri-notification-3-line me-1" />
            {t("studio.products.batches.recall.actions.notifyTeam")}
          </Button>
        </div>

        <div className="form-check mb-3">
          <Input
            id={`recall-has-phone-${productId}-${batchId ?? 0}`}
            type="checkbox"
            className="form-check-input"
            checked={hasPhoneOnly}
            onChange={handleToggleHasPhone}
          />
          <label
            className="form-check-label"
            htmlFor={`recall-has-phone-${productId}-${batchId ?? 0}`}
          >
            {t("studio.products.batches.recall.filter.hasPhoneOnly")}
          </label>
        </div>

        {internalWithoutClient > 0 ? (
          <p className="text-muted small mb-3">
            {t("studio.products.batches.recall.internalWithoutClient", {
              count: internalWithoutClient,
            })}
          </p>
        ) : null}

        {loading ? (
          <div className="text-center py-5">
            <Spinner size="sm" />
          </div>
        ) : loadError ? (
          <div className="text-center py-4">
            <p className="text-muted mb-2">
              {t("studio.products.batches.recall.error")}
            </p>
            <Button color="link" size="sm" onClick={() => void load()}>
              {t("studio.products.batches.recall.actions.retry")}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">{emptyMessage}</p>
          </div>
        ) : (
          <>
            <p className="text-muted small mb-2">
              {t("studio.products.batches.recall.matchedCount", {
                count: matchedCount,
              })}
            </p>
            <div className="table-responsive">
              <Table className="align-middle table-nowrap mb-0" size="sm">
                <thead className="table-light">
                  <tr>
                    <th>{t("studio.products.batches.recall.table.client")}</th>
                    <th>{t("studio.products.batches.recall.table.contact")}</th>
                    <th>{t("studio.products.batches.recall.table.lastDate")}</th>
                    <th>{t("studio.products.batches.recall.table.qty")}</th>
                    <th>{t("studio.products.batches.recall.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.client_id}>
                      <td>
                        <div className="fw-medium">{row.name}</div>
                        <small className="text-muted">
                          {formatPhone(row.country_code, row.phone_number)}
                        </small>
                      </td>
                      <td>
                        {row.email ? (
                          <small>{row.email}</small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>{formatDateTime(row.last_consumed_at)}</td>
                      <td>{row.total_qty_consumed}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {row.contact.tel_url ? (
                            <Button
                              tag="a"
                              href={row.contact.tel_url}
                              color="link"
                              size="sm"
                              className="p-0"
                            >
                              {t("studio.products.batches.recall.actions.call")}
                            </Button>
                          ) : null}
                          {row.contact.whatsapp_url ? (
                            <Button
                              tag="a"
                              href={row.contact.whatsapp_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="link"
                              size="sm"
                              className="p-0"
                            >
                              {t(
                                "studio.products.batches.recall.actions.whatsapp"
                              )}
                            </Button>
                          ) : null}
                          {row.contact.mailto_url ? (
                            <Button
                              tag="a"
                              href={row.contact.mailto_url}
                              color="link"
                              size="sm"
                              className="p-0"
                            >
                              {t("studio.products.batches.recall.actions.email")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {lastPage > 1 ? (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <Button
                  color="light"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  {t("studio.products.pagination.previous")}
                </Button>
                <small className="text-muted">
                  {t("studio.products.batches.recall.pagination", {
                    page,
                    lastPage,
                  })}
                </small>
                <Button
                  color="light"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() =>
                    setPage((prev) => Math.min(lastPage, prev + 1))
                  }
                >
                  {t("studio.products.pagination.next")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ProductBatchRecallOffcanvas;
